// Preset encoding/decoding utilities.
// Bit-packs design system params into a single integer,
// then encodes as base62 with a version prefix character.
// Browser-safe: no Node.js dependencies.
//
// Rules for backward compat:
//   1. Never reorder existing value arrays — only append.
//   2. New fields must have their default at index 0.
//   3. Only append new fields to the end of PRESET_FIELDS.
//   4. Stay under 53 bits total (JS safe integer limit).
//
// Ported from shadcn-ui/packages/shadcn/src/preset/preset.ts, with
// value arrays matching shadcn-vue's registry (see apps/v4/registry/*).

// Value arrays — order matters for backward compat. Never reorder, only append.
export const PRESET_BASES = ['reka'] as const

export const PRESET_STYLES = [
  'vega',
  'nova',
  'maia',
  'lyra',
  'mira',
  'luma',
  'sera',
  'rhea',
] as const

export const PRESET_BASE_COLORS = [
  'neutral',
  'stone',
  'zinc',
  'mauve',
  'olive',
  'mist',
  'taupe',
] as const

export const PRESET_THEMES = [
  'neutral',
  'stone',
  'zinc',
  'mauve',
  'olive',
  'mist',
  'taupe',
  'amber',
  'blue',
  'cyan',
  'emerald',
  'fuchsia',
  'green',
  'indigo',
  'lime',
  'orange',
  'pink',
  'purple',
  'red',
  'rose',
  'sky',
  'teal',
  'violet',
  'yellow',
] as const

export const PRESET_ICON_LIBRARIES = [
  'lucide',
  'tabler',
  'hugeicons',
  'phosphor',
  'remixicon',
] as const

// NOTE: this list is index-encoded into shareable preset codes, so existing
// entries must keep their position — only ever APPEND new fonts to the end.
// Must stay in sync with apps/v4/lib/fonts.ts, apps/v4/registry/fonts.ts and
// packages/cli/src/registry/constants.ts (FONTS).
export const PRESET_FONTS = [
  'inter',
  'geist-sans',
  'noto-sans',
  'nunito-sans',
  'figtree',
  'roboto',
  'raleway',
  'dm-sans',
  'public-sans',
  'outfit',
  'jetbrains-mono',
  'playfair-display',
  'oxanium',
  'manrope',
  'space-grotesk',
  'montserrat',
  'ibm-plex-sans',
  'source-sans-3',
  'instrument-sans',
  'geist-mono',
  'noto-serif',
  'roboto-slab',
  'merriweather',
  'lora',
] as const

export const PRESET_FONT_HEADINGS = ['inherit', ...PRESET_FONTS] as const

export const PRESET_CHART_COLORS = PRESET_THEMES

export const PRESET_RADII = [
  'default',
  'none',
  'small',
  'medium',
  'large',
] as const

export const PRESET_MENU_ACCENTS = ['subtle', 'bold'] as const

export const PRESET_MENU_COLORS = [
  'default',
  'inverted',
  'default-translucent',
  'inverted-translucent',
] as const

// V1 fields (version "a"): 39 bits.
const PRESET_FIELDS_V1 = [
  { key: 'menuColor', values: PRESET_MENU_COLORS, bits: 3 },
  { key: 'menuAccent', values: PRESET_MENU_ACCENTS, bits: 2 },
  { key: 'radius', values: PRESET_RADII, bits: 3 },
  { key: 'font', values: PRESET_FONTS, bits: 5 },
  { key: 'fontHeading', values: PRESET_FONT_HEADINGS, bits: 5 },
  { key: 'iconLibrary', values: PRESET_ICON_LIBRARIES, bits: 3 },
  { key: 'theme', values: PRESET_THEMES, bits: 5 },
  { key: 'baseColor', values: PRESET_BASE_COLORS, bits: 3 },
  { key: 'style', values: PRESET_STYLES, bits: 3 },
  { key: 'base', values: PRESET_BASES, bits: 2 },
  { key: 'chartColor', values: PRESET_CHART_COLORS, bits: 5 },
] as const

export interface PresetConfig {
  base: (typeof PRESET_BASES)[number]
  style: (typeof PRESET_STYLES)[number]
  baseColor: (typeof PRESET_BASE_COLORS)[number]
  theme: (typeof PRESET_THEMES)[number]
  chartColor: (typeof PRESET_CHART_COLORS)[number]
  iconLibrary: (typeof PRESET_ICON_LIBRARIES)[number]
  font: (typeof PRESET_FONTS)[number]
  fontHeading: (typeof PRESET_FONT_HEADINGS)[number]
  radius: (typeof PRESET_RADII)[number]
  menuAccent: (typeof PRESET_MENU_ACCENTS)[number]
  menuColor: (typeof PRESET_MENU_COLORS)[number]
}

export const DEFAULT_PRESET_CONFIG: PresetConfig = Object.fromEntries(
  PRESET_FIELDS_V1.map(f => [f.key, f.values[0]]),
) as unknown as PresetConfig

// Base62 alphabet.
const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

// Version prefixes — "a" = v1. Append new versions ("b", "c", ...) when
// appending new fields; never repurpose an existing version.
const CURRENT_VERSION = 'a'
const VALID_VERSIONS = ['a'] as const

export function toBase62(num: number) {
  if (num === 0)
    return '0'
  let result = ''
  let n = num
  while (n > 0) {
    result = BASE62[n % 62] + result
    n = Math.floor(n / 62)
  }
  return result
}

export function fromBase62(str: string) {
  let result = 0
  for (let i = 0; i < str.length; i++) {
    const idx = BASE62.indexOf(str[i]!)
    if (idx === -1)
      return -1
    result = result * 62 + idx
  }
  return result
}

// Encode a PresetConfig into a short alphanumeric code.
export function encodePreset(config: Partial<PresetConfig>) {
  const merged = { ...DEFAULT_PRESET_CONFIG, ...config }

  // Uses multiplication instead of bitwise ops (JS bitwise truncates to 32 bits).
  let bits = 0
  let offset = 0
  for (const field of PRESET_FIELDS_V1) {
    const idx = (field.values as readonly string[]).indexOf(
      merged[field.key as keyof PresetConfig] as string,
    )
    bits += (idx === -1 ? 0 : idx) * 2 ** offset
    offset += field.bits
  }

  return CURRENT_VERSION + toBase62(bits)
}

// Decode a preset code back into a PresetConfig.
export function decodePreset(code: string): PresetConfig | null {
  if (!code || code.length < 2) {
    return null
  }

  const version = code[0]
  if (!VALID_VERSIONS.includes(version as (typeof VALID_VERSIONS)[number])) {
    return null
  }

  const bits = fromBase62(code.slice(1))
  if (bits < 0)
    return null

  const result = {} as Record<string, string>
  let offset = 0
  for (const field of PRESET_FIELDS_V1) {
    const idx = Math.floor(bits / 2 ** offset) % 2 ** field.bits
    result[field.key]
      = idx < field.values.length ? field.values[idx]! : field.values[0]!
    offset += field.bits
  }

  return result as unknown as PresetConfig
}

// Check if a string looks like a preset code (version char + base62).
export function isPresetCode(value: string) {
  if (!value || value.length < 2 || value.length > 10) {
    return false
  }

  if (!VALID_VERSIONS.includes(value[0] as (typeof VALID_VERSIONS)[number])) {
    return false
  }

  for (let i = 1; i < value.length; i++) {
    if (!BASE62.includes(value[i]!)) {
      return false
    }
  }

  return true
}

// Validate that a preset code decodes successfully.
export function isValidPreset(code: string) {
  return decodePreset(code) !== null
}

// Generate a random PresetConfig.
export function generateRandomConfig(): PresetConfig {
  const pick = <T>(arr: readonly T[]) =>
    arr[Math.floor(Math.random() * arr.length)]!

  return Object.fromEntries(
    PRESET_FIELDS_V1.map(f => [f.key, pick(f.values)]),
  ) as unknown as PresetConfig
}

// Generate a random preset code.
export function generateRandomPreset() {
  return encodePreset(generateRandomConfig())
}
